import React from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { getFeatureBySlug, featureManifest } from "../featureManifest";
import { getMock } from "../components/FeatureMockups";
import FinalCTA from "../components/FinalCTA";

function OutcomesList({ outcomes }) {
  return (
    <ul className="space-y-3 mt-6">
      {outcomes.map((o) => (
        <li key={o} className="flex items-start gap-3 text-[var(--mk-text-dim)]">
          <span
            className="mt-1 inline-grid place-items-center h-5 w-5 rounded-sm flex-shrink-0"
            style={{
              background: "rgba(214,239,55,0.15)",
              border: "1px solid var(--mk-border-accent)",
              color: "var(--mk-accent)",
              fontSize: 11,
            }}
            aria-hidden="true"
          >
            ✓
          </span>
          <span className="text-sm md:text-base">{o}</span>
        </li>
      ))}
    </ul>
  );
}

function ZigZagSection({ section, index }) {
  const Mock = getMock(section.screenshotSlug);
  const reverse = index % 2 === 1;
  return (
    <section className="mk-section" data-testid={`mk-section-${section.screenshotSlug}`}>
      <div className="mk-container">
        <div className={`grid gap-10 lg:gap-16 items-center md:grid-cols-2 ${reverse ? "md:[&>*:first-child]:order-2" : ""}`}>
          <div className="mk-reveal">
            <div className="mk-mono text-[10px] font-bold uppercase tracking-[0.3em] text-[var(--mk-accent)] mb-4">
              {section.kind?.toUpperCase() === "ZAG" ? "Deep dive" : "How it works"}
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight mk-display mb-4">
              {section.headline}
            </h2>
            <p className="text-base md:text-lg text-[var(--mk-text-dim)] leading-relaxed">
              {section.body}
            </p>
            {section.screenshotCaption && (
              <div className="mk-mono text-[10px] text-[var(--mk-text-muted)] uppercase tracking-[0.2em] mt-4">
                — {section.screenshotCaption}
              </div>
            )}
          </div>
          <div className="mk-reveal">
            <Mock />
          </div>
        </div>
      </div>
    </section>
  );
}

function RelatedStrip({ current, relatedSlugs }) {
  const related = (relatedSlugs || [])
    .map((s) => featureManifest.find((f) => f.slug === s))
    .filter(Boolean)
    .slice(0, 4);
  if (!related.length) return null;
  return (
    <section className="mk-section !py-16" data-testid="mk-related-strip">
      <div className="mk-container">
        <div className="mk-mono text-[10px] font-bold uppercase tracking-[0.3em] text-[var(--mk-accent)] mb-6">
          Related features
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {related.map((f) => (
            <Link
              key={f.slug}
              to={`/product/${f.slug}`}
              className="rounded-2xl border p-5 hover:-translate-y-1 transition-all duration-200 block"
              style={{ borderColor: "var(--mk-border-subtle)", background: "var(--mk-surface)" }}
              data-testid={`mk-related-${f.slug}`}
            >
              <div className="mk-mono text-[9px] uppercase tracking-[0.25em] text-[var(--mk-text-muted)] mb-2">
                {f.category}
              </div>
              <div className="text-base font-bold mb-1">{f.nav}</div>
              <div className="text-xs text-[var(--mk-text-dim)] line-clamp-2">{f.eyebrow}</div>
              <div className="mt-4 text-xs text-[var(--mk-accent)] font-semibold">Explore →</div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function FeaturePageTemplate() {
  const { slug } = useParams();
  const feature = getFeatureBySlug(slug);
  if (!feature) return <Navigate to="/product" replace />;
  const HeroMock = feature.sections?.[0]?.screenshotSlug ? getMock(feature.sections[0].screenshotSlug) : null;

  return (
    <div data-testid={`mk-feature-${feature.slug}`}>
      {/* HERO */}
      <section className="mk-section !pb-16 relative overflow-hidden">
        <div className="mk-grid-bg absolute inset-0 opacity-60 pointer-events-none" />
        <div className="mk-container relative z-10">
          <div className="grid gap-10 lg:gap-16 items-start lg:grid-cols-2">
            <div>
              <div className="mk-mono text-[10px] font-bold uppercase tracking-[0.3em] text-[var(--mk-accent)] mb-4">
                {feature.eyebrow} · {feature.category}
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight mk-display leading-[1.05]">
                {feature.title}
              </h1>
              <p className="mt-6 text-base md:text-lg text-[var(--mk-text-dim)] leading-relaxed max-w-xl">
                {feature.subtitle}
              </p>
              <OutcomesList outcomes={feature.outcomes} />
              <div className="mt-8 flex gap-3">
                <Link to="/signup" className="mk-btn mk-btn-primary text-base px-5 py-3" data-testid={`mk-hero-cta-${feature.slug}`}>
                  Start free trial →
                </Link>
                <Link to="/product/demo" className="mk-btn mk-btn-secondary text-base px-5 py-3">
                  Book a demo
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="mk-reveal">{HeroMock ? <HeroMock /> : null}</div>
            </div>
          </div>
        </div>
      </section>

      {/* ALTERNATING SECTIONS (skip the first — already used in hero) */}
      {(feature.sections || []).slice(1).map((s, i) => (
        <ZigZagSection key={s.screenshotSlug || i} section={s} index={i} />
      ))}

      <RelatedStrip current={feature.slug} relatedSlugs={feature.related} />
      <FinalCTA headline={`Ready to ship ${feature.nav.toLowerCase()}?`} />
    </div>
  );
}
